import api from "@/configs/axios-config";

/**
 * Migration จากระบบเก่า — เรียก 2 endpoint ที่ backend ระบบใหม่ทำเพิ่ม
 * (browser ต่อ MSSQL เก่า / เรียก API เก่าตรงๆ ไม่ได้ จึงผ่าน backend proxy)
 */

export type OldPartRef = {
  part_id: number;
  supplier_code: string;
};

/** GET /migration/old-part-ids → enumerate ทุก part จาก [AMPA].[AMPA].[Parts] (order by part_id desc)
 *  ถ้าส่ง supplierCode จะกรองเฉพาะ supplier นั้น (ใช้สำหรับ test single supplier)
 *  isActive: 1 (default) = part ที่ active, 0 = part ที่ปิดไปแล้วในระบบเก่า */
export const getOldPartIds = async (
  supplierCode?: string,
  isActive: 0 | 1 = 1,
): Promise<OldPartRef[]> => {
  const res = await api.get<OldPartRef[]>("/migration/old-part-ids", {
    params: {
      ...(supplierCode ? { supplier_code: supplierCode } : {}),
      is_active: isActive,
    },
  });
  return res.data;
};

export type MigrationVersion = "v1" | "v2";

export type OldCbdFile = {
  /** base64 ของไฟล์ xlsx ที่ backend normalize zip แล้ว (exceljs โหลดได้) */
  fileArray: string;
  fileName: string;
};

/**
 * GET /migration/old-cbd/{version}/{partId}
 * backend: เรียก API เก่าโดยใช้ token จาก header `X-Legacy-Token` → normalize zip → คืน base64
 * - 404 ถ้า version นั้นไม่ถูก maintain ในระบบเก่า
 * - status อื่น + body.detail === "LEGACY_TOKEN_EXPIRED" ถ้า token เก่าหมดอายุ/ใช้ไม่ได้
 *   (ห้ามใช้ 401 เพราะ axios interceptor จะไปรีเฟรช MSAL แทน)
 */
export const LEGACY_TOKEN_EXPIRED = "LEGACY_TOKEN_EXPIRED";

export const getOldCbdFile = async (
  partId: number,
  version: MigrationVersion,
  legacyToken: string,
): Promise<OldCbdFile> => {
  const res = await api.get<OldCbdFile>(
    `/migration/old-cbd/${version}/${partId}`,
    { headers: { "X-Legacy-Token": legacyToken } },
  );
  return res.data;
};

export type LegacyMaterialCode = {
  child_part_no: string;
  material_code: string;
};

/** GET /migration/legacy-material-codes → child_part_no -> material_code ของ part เดียวจากระบบเก่า
 *  ใช้ตอน import v2 เจอ material ที่ attribute ตรงกันมากกว่า 1 ตัว เพื่อหาว่า child part นี้ใช้ code ไหนจริง
 *  คืน [] ถ้า part นี้ไม่เคยมีในระบบเก่า (part ใหม่ที่ import ตามปกติ ไม่ใช่ migration) */
export const getLegacyMaterialCodes = async (
  partNo: string,
  ac: string,
  rt: string,
  supplierCode: string,
): Promise<LegacyMaterialCode[]> => {
  const res = await api.get<LegacyMaterialCode[]>(
    "/migration/legacy-material-codes",
    { params: { part_no: partNo, ac, rt, supplier_code: supplierCode } },
  );
  return res.data;
};

export type LegacyPartActiveRef = {
  part_no: string;
  ac_code: string;
  rt_code: string;
  is_active: boolean;
};

/** GET /migration/legacy-part-active → part_no/ac/rt/is_active ของ part ทุกตัวใน supplier เดียวจากระบบเก่า
 *  ใช้โดย ExRate migration เพื่อ tag แต่ละแถวในไฟล์ CBD ว่า active/inactive (ไฟล์เองไม่มีคอลัมน์นี้) */
export const getLegacyPartActive = async (
  supplierCode: string,
): Promise<LegacyPartActiveRef[]> => {
  const res = await api.get<LegacyPartActiveRef[]>("/migration/legacy-part-active", {
    params: { supplier_code: supplierCode },
  });
  return res.data;
};
